import { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { artistsList, artistCategories } from '@/mocks/artists';
import { Link } from 'react-router-dom';

export default function ArtistsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedArtist, setSelectedArtist] = useState<typeof artistsList[0] | null>(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Artists – Ganga Bhakti | Bhojpuri, Maithili & Hindi Devotional Music Artists';

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'page-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Ganga Bhakti Artists – Bhojpuri, Maithili & Hindi Devotional Music Artists',
      url: `${import.meta.env.VITE_SITE_URL || ''}/artists`,
      description: '29 legendary artists including Sadhna Sargam, Suresh Wadkar, Ravindra Jain and more. Bhojpuri folk, Maithili folk, and Hindi Devotional music artists managed by Ganga Bhakti.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || ''}/` },
          { '@type': 'ListItem', position: 2, name: 'Artists', item: `${import.meta.env.VITE_SITE_URL || ''}/artists` },
        ],
      },
      mainEntity: {
        '@type': 'ItemList',
        name: 'Ganga Bhakti Artist Roster',
        numberOfItems: artistsList.length,
        itemListElement: artistsList.map((artist, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'MusicGroup',
            name: artist.name,
            genre: artist.genre,
          },
        })),
      },
    });
    const existing = document.getElementById('page-jsonld');
    if (existing) existing.remove();
    document.head.appendChild(script);

    const t = setTimeout(() => setVisible(true), 100);
    return () => {
      clearTimeout(t);
      const el = document.getElementById('page-jsonld');
      if (el) el.remove();
    };
  }, []);

  const filtered = activeCategory === 'All' ? artistsList : artistsList.filter((a) => a.category === activeCategory);

  const artistBios: Record<string, string> = {
    'Poonam Mishra': 'A celebrated Maithili folk singer whose melodious voice has captivated millions across Bihar and Jharkhand. Known for her authentic renditions of traditional Maithili songs, Poonam Mishra has been a cornerstone of Ganga Bhakti\'s folk music catalog for over two decades.',
    'Kumkum': 'One of the most beloved Bhojpuri folk artists, Kumkum\'s vibrant performances and soulful voice have made her a household name across North India. Her recordings with Ganga Bhakti have garnered hundreds of millions of views.',
    'Madhav Rai': 'A Hindi Devotional music maestro whose bhajans and kirtans have brought spiritual solace to millions. Madhav Rai\'s deep, resonant voice and profound understanding of Hindi Devotional music make him one of Ganga Bhakti\'s most treasured artists.',
    'Dilip Darbhangiya': 'Hailing from the cultural heartland of Darbhanga, Dilip Darbhangiya is a master of classical Maithili music. His scholarly approach to folk traditions has earned him recognition as a cultural ambassador of Mithila.',
    'Ram Babu Jha': 'A veteran Maithili folk artist whose career spans over three decades. Ram Babu Jha\'s authentic portrayal of rural Maithili life through music has made him an irreplaceable voice in the folk music landscape.',
    'Kunj Bihari Mishra': 'A classical vocalist of exceptional caliber, Kunj Bihari Mishra brings the rigor of classical training to folk music. His performances are celebrated for their technical precision and emotional depth.',
    // 'Rashmi Dutt': 'A versatile Hindi Devotional singer whose repertoire spans bhajans, aartis, and Hindi Devotional songs across multiple traditions. Rashmi Dutt\'s pure voice and Hindi Devotional sincerity have earned her a dedicated following.',
    'Salim Miyan': 'A master of Sufi and folk traditions, Salim Miyan\'s qawwalis and folk songs bridge communities and traditions. His powerful performances carry the essence of centuries-old musical heritage.',
    'Lallan Ram': 'A popular Bhojpuri folk artist known for his energetic performances and relatable lyrics. Lallan Ram\'s music captures the spirit of rural Bhojpuri culture with authenticity and joy.',
    'Gautam Lahar': 'A dynamic performer in the folk and nautanki tradition, Gautam Lahar brings theatrical energy to his musical performances. His unique style blends storytelling with musical excellence.',
    'Maya Upadhay': 'An Awadhi folk singer whose gentle voice and poetic lyrics paint vivid pictures of life in the Awadh region. Maya Upadhay\'s music is a celebration of the rich cultural heritage of Uttar Pradesh.',
    // 'Manager Byas': 'A scholar-artist who has dedicated his life to preserving and promoting classical Maithili music. Manager Byas\'s deep knowledge of Maithili literary traditions enriches every performance.',
    // 'Rashmi Yogini': 'A spiritual music artist who blends yoga philosophy with Hindi Devotional music. Rashmi Yogini\'s meditative compositions have found a global audience seeking inner peace through music.',
    // 'Ramashankar Yadav': 'A beloved Bhojpuri folk artist whose music resonates with the working class of North India. Ramashankar Yadav\'s authentic voice and relatable themes have made him a people\'s artist.',
    // 'Avinash Karn': 'A contemporary Maithili artist who bridges traditional folk music with modern sensibilities. Avinash Karn\'s innovative approach has attracted younger audiences to Maithili music.',
    'Bijender Giri': 'A Hindi Devotional bhajan singer whose heartfelt renditions of sacred songs have touched millions of devotees. Bijender Giri\'s music is a testament to the power of Hindi Devotional music in Indian culture.',
    'Badal Bawali': 'A folk comedy artist who uses humor and music to comment on social issues. Badal Bawali\'s unique blend of comedy and folk music has made him one of the most entertaining artists in the Bhojpuri circuit.',
    'Guddu Rangila': 'A vibrant Bhojpuri entertainer whose colorful performances and catchy songs have made him a crowd favorite. Guddu Rangila\'s energy on stage is legendary in the folk music world.',
    'Kamawas Kunwar': 'A Rajasthani folk artist who brings the desert\'s soul to life through music. Kamawas Kunwar\'s performances are a celebration of Rajasthani culture, complete with traditional instruments and costumes.',
    'Praveen Samrat': 'A rising star in the Bhojpuri music scene, Praveen Samrat combines traditional folk elements with contemporary production. His music has quickly gained a massive following among younger audiences.',
    'Sadhna Sargam': 'A legendary Bollywood playback singer whose golden voice has graced hundreds of Hindi film songs. Sadhna Sargam\'s association with Ganga Bhakti has brought her Hindi Devotional music to millions of new listeners.',
    'Meenu Arora': 'A versatile singer who excels in both Hindi Devotional and folk genres. Meenu Arora\'s warm voice and expressive singing style have made her a beloved figure in Indian music.',
    'Suresh Wadkar': 'A legendary Bollywood playback singer and Hindi Devotional music artist. Suresh Wadkar\'s powerful voice and impeccable technique have made him one of India\'s most respected musicians.',
    'Ravindra Jain': 'A legendary music composer and singer whose contributions to Indian film and Hindi Devotional music are immeasurable. Ravindra Jain\'s timeless compositions continue to inspire generations of musicians.',
    'Vivek Vashisht': 'A Hindi Devotional music artist whose sincere and heartfelt renditions of bhajans and kirtans have earned him a devoted following. Vivek Vashisht\'s music is a bridge between the divine and the devotee.',
    'Swastika Mishra': 'An emerging Maithili folk artist who is carrying forward the rich traditions of Mithila music. Swastika Mishra\'s fresh voice and authentic approach have quickly made her a fan favorite.',
    'Shikha Rana': 'A contemporary bhakti artist who blends traditional Hindi Devotional music with modern production. Shikha Rana\'s music appeals to both traditional devotees and younger audiences.',
    'Nikhil Mahadev Jha': 'A classical Maithili musician whose scholarly approach and technical mastery have earned him recognition as one of the finest exponents of Maithili classical music.',
    'Soni Chaudhary': 'A lively Bhojpuri folk artist whose energetic performances and catchy songs have made her one of the most popular artists in the Bhojpuri music circuit.',
  };

  return (
    <div className="bg-[#0d0d0d] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            alt="Ganga Bhakti artists collection"
            className="w-full h-full object-cover object-top"
            src="https://readdy.ai/api/search-image?query=Indian%20music%20artists%20performing%20on%20stage%2C%20dramatic%20stage%20lighting%20golden%20spotlights%2C%20multiple%20performers%2C%20cinematic%20concert%20atmosphere%2C%20dark%20background%20with%20golden%20light%20rays&width=1920&height=600&seq=artists_hero&orientation=landscape"
          />
          <div className="absolute inset-0 bg-black/85"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[#d4af37]"></span>
              <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">Our Roster</span>
              <span className="w-8 h-px bg-[#d4af37]"></span>
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white mb-4">
              Our <span className="text-[#d4af37]">Artists</span>
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              29 legendary voices. From Bollywood playback legends to folk music icons — meet the artists who define Ganga Bhakti's musical legacy.
            </p>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-[#080808] border-b border-[#d4af37]/10 sticky top-16 md:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 overflow-x-auto pb-1 flex-nowrap">
            {artistCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${activeCategory === cat ? 'btn-gold text-black' : 'border border-[#d4af37]/20 text-white/60 hover:text-white hover:border-[#d4af37]/40'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Artists Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6 text-white/40 text-sm">
            Showing {filtered.length} artists
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {filtered.map((artist, i) => (
              <button
                key={artist.id}
                onClick={() => setSelectedArtist(artist)}
                className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-500 text-left ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${Math.min(i * 40, 400)}ms` }}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    alt={`${artist.name} - Ganga Bhakti artist`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    src={artist.img}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent"></div>
                <div className="absolute inset-0 bg-[#d4af37]/0 group-hover:bg-[#d4af37]/10 transition-all duration-300 border border-transparent group-hover:border-[#d4af37]/30 rounded-2xl"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="text-white font-semibold text-sm font-heading leading-tight">{artist.name}</div>
                  <div className="text-[#d4af37] text-xs mt-0.5">{artist.genre}</div>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-information-line text-[#d4af37] text-sm"></i>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Artist Modal */}
      {selectedArtist && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setSelectedArtist(null)}
        >
          <div
            className="bg-[#111] border border-[#d4af37]/20 rounded-3xl max-w-2xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col sm:flex-row">
              <div className="sm:w-56 flex-shrink-0">
                <img
                  alt={selectedArtist.name}
                  className="w-full h-64 sm:h-full object-cover object-top"
                  src={selectedArtist.img}
                />
              </div>
              <div className="flex-1 p-6 md:p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-white">{selectedArtist.name}</h2>
                    <div className="text-[#d4af37] text-sm mt-1">{selectedArtist.genre}</div>
                    <div className="inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/20">
                      <span className="text-[#d4af37] text-xs">{selectedArtist.category}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedArtist(null)}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-white text-sm"></i>
                  </button>
                </div>
                <p className="text-white/70 text-sm leading-relaxed mb-6">
                  {artistBios[selectedArtist.name] || `A celebrated artist in the ${selectedArtist.genre} tradition, ${selectedArtist.name} has been a valued member of the Ganga Bhakti family, contributing to the label's rich musical legacy.`}
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-black whitespace-nowrap btn-gold cursor-pointer transition-all hover:scale-105"
                  onClick={() => setSelectedArtist(null)}
                >
                  Book This Artist <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-[#080808]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Want to Join Our <span className="text-[#d4af37]">Artist Roster?</span>
          </h2>
          <p className="text-white/60 text-lg mb-8">
            We're always looking for exceptional talent. Submit your profile and let's create something legendary together.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-black whitespace-nowrap btn-gold cursor-pointer transition-all hover:scale-105">
            Apply Now <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
